/**
 * ConversationHistory — 대화 히스토리 사이드바
 *
 * useAgentSessions 사용, 검색/정렬/삭제/아카이브
 */

import { useState } from "react";
import { Search, Trash2, Archive, MessageSquare, X, Plus } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  useAgentSessions,
  useDeleteSession,
  useArchiveSession,
} from "@/agent/data/sessions";
import type { AgentSession } from "@modootoday/core-agent/types";
import { PRESET_AGENTS } from "@modootoday/core-agent/agents";

/* ──── Agent ID → 한국어 이름 / 컬러 매핑 ──── */

const AGENT_BADGE_COLOR: Record<string, string> = {
  "keyword-analyst":
    "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300",
  "dashboard-briefer":
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
  "content-auditor":
    "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
  "bid-optimizer":
    "bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300",
  "trend-forecaster":
    "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
  "channel-scout":
    "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
  "serp-tracker":
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300",
  "content-creator":
    "bg-pink-100 text-pink-700 dark:bg-pink-950/60 dark:text-pink-300",
  "full-analyst":
    "bg-slate-200 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200",
  "help-guide":
    "bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300",
};

function getAgentLabel(agentId: string): string {
  const preset = PRESET_AGENTS.find((a) => a.id === agentId);
  return preset?.name ?? agentId;
}

function getAgentBadgeColor(agentId: string): string {
  return AGENT_BADGE_COLOR[agentId] ?? "bg-surface-sunken text-text-tertiary";
}

export interface ConversationHistoryProps {
  currentSessionId?: string | null;
  onSelectSession: (sessionId: string) => void;
  onNewChat?: () => void;
  onClose?: () => void;
  agentId?: string;
  className?: string;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString("ko-KR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (diffDays < 7) return `${diffDays}일 전`;
  return date.toLocaleDateString("ko-KR", { month: "short", day: "numeric" });
}

export function ConversationHistory({
  currentSessionId,
  onSelectSession,
  onNewChat,
  onClose,
  agentId,
  className,
}: ConversationHistoryProps) {
  const [search, setSearch] = useState("");
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const { data: sessions = [], isLoading } = useAgentSessions(agentId);
  const deleteSession = useDeleteSession();
  const archiveSession = useArchiveSession();

  const filtered = sessions.filter(
    (s) =>
      s.status !== "deleted" &&
      (search === "" || s.title.toLowerCase().includes(search.toLowerCase())),
  );

  // 날짜 그룹핑
  const now = new Date();
  const today = filtered.filter((s) => {
    const d = new Date(s.lastMessageAt);
    return d.toDateString() === now.toDateString();
  });
  const older = filtered.filter((s) => {
    const d = new Date(s.lastMessageAt);
    return d.toDateString() !== now.toDateString();
  });

  function handleDelete(e: React.MouseEvent, sessionId: string) {
    e.stopPropagation();
    deleteSession.mutate(sessionId);
  }

  function handleArchive(e: React.MouseEvent, sessionId: string) {
    e.stopPropagation();
    archiveSession.mutate(sessionId);
  }

  return (
    <div className={cn("flex h-full flex-col bg-surface-raised", className)}>
      {/* 헤더 — h-14로 통일 (Rail 헤더, AppLayout NavPanel 헤더와 동일) */}
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-stroke-subtle px-4">
        <h3 className="text-[13px] font-semibold text-text-primary">
          대화 히스토리
        </h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex size-7 items-center justify-center rounded-lg text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {/* 새 대화 CTA */}
      {onNewChat && (
        <div className="border-b border-stroke-subtle px-3 py-3">
          <button
            type="button"
            onClick={onNewChat}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-accent/20 bg-accent/5 px-3 py-2 text-[12px] font-semibold text-text-brand transition-colors hover:bg-accent/10"
          >
            <Plus className="size-3.5" />
            <span>새 대화 시작</span>
          </button>
        </div>
      )}

      {/* 검색 */}
      <div className="border-b border-stroke-subtle px-3 py-2">
        <div className="flex items-center gap-2 rounded-lg bg-surface-secondary/60 px-3 py-2">
          <Search className="size-3.5 shrink-0 text-text-tertiary" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="대화 검색..."
            className="flex-1 bg-transparent text-[12px] text-text-primary outline-none placeholder:text-text-tertiary"
          />
        </div>
      </div>

      {/* 목록 */}
      <div className="flex-1 overflow-y-auto py-2">
        {isLoading && (
          <div className="px-4 py-8 text-center">
            <p className="text-[12px] text-text-tertiary">불러오는 중...</p>
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="px-4 py-8 text-center">
            <MessageSquare className="mx-auto mb-2 size-8 text-text-tertiary/40" />
            <p className="text-[12px] text-text-tertiary">
              {search ? "검색 결과가 없습니다" : "대화 히스토리가 없습니다"}
            </p>
          </div>
        )}

        {today.length > 0 && (
          <SessionGroup
            label="오늘"
            sessions={today}
            currentSessionId={currentSessionId}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            onSelect={onSelectSession}
            onDelete={handleDelete}
            onArchive={handleArchive}
          />
        )}

        {older.length > 0 && (
          <SessionGroup
            label="이전"
            sessions={older}
            currentSessionId={currentSessionId}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            onSelect={onSelectSession}
            onDelete={handleDelete}
            onArchive={handleArchive}
          />
        )}
      </div>
    </div>
  );
}

function SessionGroup({
  label,
  sessions,
  currentSessionId,
  hoveredId,
  setHoveredId,
  onSelect,
  onDelete,
  onArchive,
}: {
  label: string;
  sessions: AgentSession[];
  currentSessionId?: string | null;
  hoveredId: string | null;
  setHoveredId: (id: string | null) => void;
  onSelect: (id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onArchive: (e: React.MouseEvent, id: string) => void;
}) {
  return (
    <div className="mb-2">
      <p className="px-4 py-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </p>
      {sessions.map((session) => (
        <SessionItem
          key={session.id}
          session={session}
          isActive={session.id === currentSessionId}
          isHovered={hoveredId === session.id}
          onHover={setHoveredId}
          onSelect={onSelect}
          onDelete={onDelete}
          onArchive={onArchive}
        />
      ))}
    </div>
  );
}

function SessionItem({
  session,
  isActive,
  isHovered,
  onHover,
  onSelect,
  onDelete,
  onArchive,
}: {
  session: AgentSession;
  isActive: boolean;
  isHovered: boolean;
  onHover: (id: string | null) => void;
  onSelect: (id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onArchive: (e: React.MouseEvent, id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(session.id)}
      onMouseEnter={() => onHover(session.id)}
      onMouseLeave={() => onHover(null)}
      className={cn(
        "group flex w-full items-start gap-2.5 px-4 py-2.5 text-left transition-colors",
        isActive
          ? "bg-accent/8 text-text-brand"
          : "text-text-primary hover:bg-surface-secondary",
      )}
    >
      <MessageSquare
        className={cn(
          "mt-0.5 size-3.5 shrink-0",
          isActive ? "text-text-brand" : "text-text-tertiary",
        )}
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="truncate text-[12px] font-medium">{session.title}</p>
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              "inline-flex shrink-0 items-center rounded px-1.5 py-[1px] text-[9px] font-medium",
              getAgentBadgeColor(session.agentId),
            )}
          >
            {getAgentLabel(session.agentId)}
          </span>
          <span className="truncate text-[10px] text-text-tertiary">
            {formatDate(session.lastMessageAt)} · {session.messageCount}개
          </span>
        </div>
      </div>

      {/* 액션 버튼 (호버 시) */}
      {isHovered && (
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={(e) => onArchive(e, session.id)}
            title="아카이브"
            className="flex size-5 items-center justify-center rounded text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
          >
            <Archive className="size-3" />
          </button>
          <button
            type="button"
            onClick={(e) => onDelete(e, session.id)}
            title="삭제"
            className="flex size-5 items-center justify-center rounded text-text-tertiary hover:bg-signal-danger/10 hover:text-signal-danger"
          >
            <Trash2 className="size-3" />
          </button>
        </div>
      )}
    </button>
  );
}
